import React from 'react'
import { css } from '@emotion/react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import cn from 'classnames'

import Draft from 'inbox/draft'

import { ENTER, TAB } from 'constants/keys'

import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import AssignmentDropdown from 'components/App/DesktopView/shared/AssignmentDropdown/ReplyPage'
import { Flex } from 'shared/ui'

import { runOnNextTick } from 'util/functions'
import { isCmdEnter, stopEvent } from 'util/keys'

import EmojiPicker from 'components/App/DesktopView/shared/EmojiPicker'
import ToggleKbSearch from 'ducks/ticketing/components/ToggleKbSearch'
import withDropzone from 'components/Dropzone'

import AttachmentIcon from '../shared/AttachmentIcon'
import IconWithTooltip from '../shared/IconWithTooltip'
import OverflowingDivider from '../shared/OverflowingDivider'
import AlignedToolbar from '../shared/AlignedToolbar'

import Editor from './Editor'
import Button from './Button'
import styles from './styles.css'

import EditorDropzoneIndicator from '../shared/EditorDropzoneIndicator'
import { handleDrop } from '../shared/dragAndDrop'

// HACK (jscheel): No idea why we need this, but sui is not adding the margin
// here for some reason, even though it is on the reply form version.
const OverflowingDividerCSS = {
  marginTop: '1rem',
}

const FlexWithDropzone = withDropzone(Flex, {
  indicatorComponent: EditorDropzoneIndicator,
  className: cn(styles.EditorDropzone, 'Dropzone'),
})

const FooterWithDropzone = withDropzone(Flex, {
  skipIndicator: true,
})

class NoteComposer extends React.Component {
  static propTypes = {
    doUpdateAgentMention: PropTypes.func.isRequired,
    draft: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    editorBottom: PropTypes.node,
    focusWhenInitialized: PropTypes.bool.isRequired,
    isDraftSendable: PropTypes.bool.isRequired,
    isMentioning: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired, // is ticket open
    onSendStart: PropTypes.func,
    onSendEnd: PropTypes.func,
    onDeleteDraft: PropTypes.func, // The Log Conversation form shows this.
    updateDraftDelayed: PropTypes.func.isRequired,
    updateAndSendDraft: PropTypes.func.isRequired,
    ticketId: PropTypes.string.isRequired,
  }

  static defaultProps = {
    editorBottom: null,
    focusWhenInitialized: true,
    onDeleteDraft: undefined,
    onSendStart: undefined,
    onSendEnd: undefined,
  }

  state = {
    word: null,
  }

  setStateAndSend = state => {
    return this.updateAndSend({ state })
  }

  getContent() {
    return this.editorRef && this.editorRef.getContent()
  }

  handleSend = () => {
    this.updateAndSend()
  }

  openAndSend = () => this.setStateAndSend('opened')

  closeAndSend = () => this.setStateAndSend('closed')

  snoozeAndSend = snoozeUntil => {
    return this.updateAndSend({ snoozeUntil, state: 'closed' })
  }

  updateAndSend = changes => {
    const { ticketId, updateAndSendDraft, onSendStart, onSendEnd } = this.props
    if (onSendStart) onSendStart()
    // Its possible that we have debounced body updates not yet applied.
    // So apply those immediately, then send the draft.
    const allChanges = {
      ...changes,
      body: this.getContent(),
    }

    return updateAndSendDraft(ticketId, allChanges).catch(() => {
      if (onSendEnd) onSendEnd()
    })
  }

  handleMention = mention => {
    const { doUpdateAgentMention } = this.props
    const found = mention

    if (!found) {
      if (this.state.word) this.setState({ word: null })
      return this.closeMentionModalUnlessClosed()
    }

    const word = found.word

    if (word.charAt(0) === '@') {
      this.setState({ word: found })
      return doUpdateAgentMention(found)
    }

    return this.closeMentionModalUnlessClosed()
  }

  closeMentionModalUnlessClosed = () => {
    if (!this.props.isMentioning) return
    this.props.doUpdateAgentMention(null)
  }

  handleEditorKeyDown = e => {
    // do not re-do the whole mention UI, we're going to dispatch a keydown event
    // so that the Mentions dropdown will capture it and behave like it did before
    // it's needed because tinymce events happen within an iframe
    const event = document.createEvent('Event')
    const type = e.type

    // can bubble, and is cancellable
    event.initEvent(`editor-${type}`, true, true)
    event.forwarded = e
    document.dispatchEvent(event)

    if ((e.keyCode === TAB || e.keyCode === ENTER) && this.state.word) {
      const {
        doMentionAgent,
        selectedMentionSelectorAgentId,
        suppressMentionSelector,
      } = this.props

      if (!suppressMentionSelector) {
        doMentionAgent(selectedMentionSelectorAgentId, ' ')
        e.preventDefault()
        e.stopPropagation()
      }
    }

    if (isCmdEnter(e)) {
      stopEvent(e)
      /**
       * pnagy: we need this here, because we need to let Tinymce to go through
       * its keyhandlers before we do our job here. Our job includes
       * navigation to the TICKET_PAGE and that will remove the editor before
       * it can finish
       * */
      runOnNextTick(this.closeAndSend)
    }
    return true
  }

  handleChange = (text, { firstEdit, deletedAll } = {}) => {
    const { updateDraftDelayed, ticketId } = this.props
    updateDraftDelayed(ticketId, 'body', text, { firstEdit, deletedAll })
  }

  handleDeleteDraft = () => {
    const { draft, onDeleteDraft, ticketId } = this.props

    if (!Draft.isEmpty(draft)) {
      const msg = 'Are you sure you want to delete this draft?'
      if (!window.confirm(msg)) return false // eslint-disable-line no-alert
    }

    const ref = this.editorRef

    if (ref) ref.setContent('')
    if (onDeleteDraft) onDeleteDraft(ticketId)
    if (ref) ref.focus()

    return true
  }

  saveEditorRef = node => (this.editorRef = node)

  /* Needed to ensure the editor rerenders when we change tickets */
  editorKey = () => {
    return `note-editor-ticket-${this.props.draft.ticketId}`
  }

  render() {
    const {
      className,
      config,
      draft,
      editorBottom,
      focusWhenInitialized,
      isDraftSendable,
      isOpen,
      onDeleteDraft,
    } = this.props

    return (
      <Flex className={cn(className, 'NoteComposer grui flex-col')}>
        <FlexWithDropzone
          className="editorAndAttachments grui flex-col flex-2-1-auto"
          onDropCustom={handleDrop}
        >
          <Editor
            config={config}
            content={draft.body}
            focusWhenInitialized={focusWhenInitialized}
            forwardRef={this.saveEditorRef}
            key={this.editorKey()}
            onChange={this.handleChange}
            onKeyDown={this.handleEditorKeyDown}
            onMention={this.handleMention}
          />
          {editorBottom}
        </FlexWithDropzone>
        <OverflowingDivider horizontal css={OverflowingDividerCSS} />
        <FooterWithDropzone className="grui footer mt-4">
          <AlignedToolbar>
            <div className="grui mr-3">
              <AttachmentIcon forNote />
            </div>
            {onDeleteDraft && (
              <IconWithTooltip
                hoverColor="red"
                name="delete"
                onClick={this.handleDeleteDraft}
                tooltip="Delete"
                tooltipPosition="top"
              />
            )}
            <div className="grui mr-3">
              <EmojiPicker
                css={css`
                  padding-top: 2px;
                  margin-left: 3px;
                `}
              />
            </div>
            <ToggleKbSearch
              css={css`
                margin-left: 3px;
              `}
            />
          </AlignedToolbar>
          <Flex className="grui justify-end flex-1-0-auto">
            <AssignmentDropdown
              direction="left"
              drilledDownGroupId={draft.assigned_group_id}
              key="assignment"
              trigger={
                <div>
                  <AssignmentBadge
                    assignment={draft.assignment}
                    bgColor="white"
                    iconSize={24}
                    tooltipPosition="top"
                  />
                </div>
              }
              upward
            />
            <div className="grui ml-4">
              <Button
                disabled={!isDraftSendable}
                isOpen={isOpen}
                onSend={this.handleSend}
                onSendClosedClick={this.closeAndSend}
                onSendOpenClick={this.openAndSend}
                onSnoozeOptionClick={this.snoozeAndSend}
              />
            </div>
          </Flex>
        </FooterWithDropzone>
      </Flex>
    )
  }
}

export default styled(NoteComposer)`
  min-height: 73px; /* note: this number must work with the placholder/reply form height too */

  .editorAndAttachments {
    overflow-y: auto;
    min-height: ${props => props.placeholderHeight || 50}px;
    margin-bottom: ${props => props.theme.spacing['5']};
  }

  .footer {
    flex: 0 0 auto;
  }
`
